/* eslint-disable sort-keys */
import { registerClass } from '../SatCoreRegistry';

import { getSessionStorageItem, setSessionStorageItem } from '../utils/session';

import { ELEMENT_TYPE } from '../managers/CourseManager';

export default class VerticalCourseTOCService {
  /**
   * Compute the classroom-scoped sessionStorage key that stores the vertical
   * TOC's saved location. One entry per classroom, mirroring the classic
   * c2c_lastCourseLocation_* pattern. Payload holds
   * { courseId, courseElementId, expandedCourseElementIds }.
   */
  static getLastVerticalCourseTOCLocationSessionStorageKey = (classroomId) => {
    return `c2c_lastVerticalCourseTOCLocation_${classroomId}`;
  }

  /**
   * Read the persisted vertical TOC location for the given classroom.
   * Returns the parsed payload object, or null if no entry exists / invalid.
   */
  static getLastVerticalCourseTOCLocation = (classroomId) => {
    const key = this.getLastVerticalCourseTOCLocationSessionStorageKey(classroomId);
    const locationStr = getSessionStorageItem(key);
    if (!locationStr) {
      return null;
    }
    const parsed = JSON.parse(locationStr);
    return (parsed && typeof parsed === 'object') ? parsed : null;
  }

  /**
   * Persist the vertical TOC location for the given classroom. Overwrites
   * whatever was there — single-slot per classroom, mirroring classic.
   * The manager holds expanded IDs as a Set; convert to a flat array at
   * the JSON boundary.
   */
  static saveLastVerticalCourseTOCLocation = (classroomId, courseId, courseElementId, expandedCourseElementIds) => {
    if (!classroomId || !courseId) {
      return;
    }
    const key = this.getLastVerticalCourseTOCLocationSessionStorageKey(classroomId);
    const expandedCourseElementIdsArray = Array.isArray(expandedCourseElementIds)
      ? expandedCourseElementIds
      : Array.from(expandedCourseElementIds || []);
    const location = {
      courseId,
      expandedCourseElementIds: expandedCourseElementIdsArray,
      courseElementId
    };
    setSessionStorageItem(key, JSON.stringify(location));
  }

  /**
   * Build a lookup structure over the course tree in one depth-first pass.
   * Callers should memoize the result against fullCourseResource identity
   * (e.g. useMemo) so subsequent helper calls are O(1) Map lookups.
   * @param {Object} fullCourseResource - The nested course tree (root node).
   * @returns {{
   *   rootId: string | null,
   *   flatElements: Object[],
   *   elementByIdMap: Map<string, Object>,
   *   ancestorPathByIdMap: Map<string, string[]>
   * }}
   */
  static buildVerticalCourseTOCTreeLookup = (fullCourseResource) => {
    const flatElements = [];
    const elementByIdMap = new Map();
    const ancestorPathByIdMap = new Map();

    const collect = (courseTOCElement, ancestorIds) => {
      flatElements.push(courseTOCElement);
      elementByIdMap.set(courseTOCElement.id, courseTOCElement);
      ancestorPathByIdMap.set(courseTOCElement.id, ancestorIds);
      (courseTOCElement.CourseResourceElements || []).forEach((childCourseTOCElement) => {
        collect(childCourseTOCElement, [...ancestorIds, courseTOCElement.id]);
      });
    };

    if (fullCourseResource) {
      collect(fullCourseResource, []);
    }

    const rootId = fullCourseResource?.id || null;
    const verticalCourseTOCTreeLookup = {
      rootId,
      flatElements,
      elementByIdMap,
      ancestorPathByIdMap
    };

    return verticalCourseTOCTreeLookup;
  }

  /**
   * Compute the branch IDs required to reveal the first branch (in
   * depth-first order from root) that directly contains any leaf children.
   * Used as the initial TOC tree expansion when no saved state and no target
   * element ID.
   * @param {Object} verticalCourseTOCTreeLookup - Result of buildVerticalCourseTOCTreeLookup.
   * @returns {string[]} Ordered branch IDs; empty if the tree has no content.
   */
  static getExpandedIdsForInitialBranch = (verticalCourseTOCTreeLookup) => {
    const { rootId, flatElements, ancestorPathByIdMap } = verticalCourseTOCTreeLookup;
    const firstContentBranch = flatElements.find((courseTOCElement) => (
      ELEMENT_TYPE.isBranchType(courseTOCElement) &&
      courseTOCElement.type !== ELEMENT_TYPE.ROOT &&
      (courseTOCElement.CourseResourceElements || []).some((child) => !ELEMENT_TYPE.isBranchType(child))
    ));
    if (!firstContentBranch) {
      return [];
    }
    const ancestorIds = ancestorPathByIdMap.get(firstContentBranch.id) || [];
    return [
      ...ancestorIds.filter((id) => id !== rootId),
      firstContentBranch.id
    ];
  }

  /**
   * Compute the branch IDs required to reveal the given element. Used on
   * normal mount when the URL / navigation supplies a target, and on
   * gradebook "back to course" deep-link entry when the target is already
   * present in the loaded tree.
   * @param {Object} verticalCourseTOCTreeLookup - Result of buildVerticalCourseTOCTreeLookup.
   * @param {string} courseTOCElementId - Target element ID.
   * @returns {string[]} Ordered branch IDs; empty if not found.
   */
  static getExpandedIdsForElementId = (verticalCourseTOCTreeLookup, courseTOCElementId) => {
    const { rootId, ancestorPathByIdMap } = verticalCourseTOCTreeLookup;
    if (!courseTOCElementId) {
      return [];
    }
    const ancestorIds = ancestorPathByIdMap.get(courseTOCElementId) || [];
    return ancestorIds.filter((id) => id !== rootId);
  }
}

registerClass('VerticalCourseTOCService', VerticalCourseTOCService);
